/**
 * VersionChecker - Detects when a new app version is deployed
 * Checks version.json periodically and prompts user to refresh when updated
 */

import { Logger } from './Logger';
import { ToasterService } from './ToasterService';
import { ZIndex } from '../constants/ZIndex';

const logger = Logger.scope('VersionChecker');

// Check interval: 5 minutes
const CHECK_INTERVAL_MS = 5 * 60 * 1000;

// Version loaded at startup
let currentVersion: string | null = null;
let checkIntervalId: number | null = null;

// Use sessionStorage to persist updateToastShown across HMR reloads
const UPDATE_SHOWN_KEY = 'flexiva_update_toast_shown';
const isUpdateToastShown = (): boolean => sessionStorage.getItem(UPDATE_SHOWN_KEY) === 'true';
const setUpdateToastShown = (shown: boolean): void => sessionStorage.setItem(UPDATE_SHOWN_KEY, shown ? 'true' : 'false');

// Track if we've already initialized (prevent multiple interval registrations)
let isInitialized = false;

/**
 * Fetch the current version from version.json
 */
async function fetchVersion(): Promise<string | null> {
  try {
    // Add timestamp to bypass cache
    const response = await fetch(`/version.json?t=${Date.now()}`);
    if (!response.ok) return null;

    const data = await response.json();
    return data.version || null;
  } catch (error) {
    logger.debug('Failed to fetch version:', error);
    return null;
  }
}

/**
 * Check if a new version is available
 */
async function checkForUpdate(): Promise<void> {
  if (isUpdateToastShown()) return; // Already showing update notification

  const serverVersion = await fetchVersion();
  if (!serverVersion || !currentVersion) return;

  if (serverVersion !== currentVersion) {
    logger.info(`New version available: ${serverVersion} (current: ${currentVersion})`);
    showUpdateNotification(serverVersion);
  }
}

/**
 * Show update notification to user
 */
function showUpdateNotification(newVersion: string): void {
  // Mark as shown in sessionStorage (persists across HMR reloads)
  setUpdateToastShown(true);
  
  // Remove any existing update toasts first
  document.querySelectorAll('[id^="update-toast-"]').forEach(el => el.remove());
  
  // Create a persistent toast with refresh button
  const toastId = `update-toast-${Date.now()}`;
  const container = document.getElementById('toast-container') || createToastContainer();
  
  const toast = document.createElement('div');
  toast.id = toastId;
  toast.className = 'alert alert-info shadow-lg flex items-center gap-3';
  // Add pointer-events: auto and position: relative to ensure clicks work
  toast.style.cssText = 'pointer-events: auto; position: relative;';
  toast.innerHTML = `
    <i class="fas fa-sync-alt text-lg"></i>
    <div class="flex-1">
      <div class="font-semibold">Update Available</div>
      <div class="text-sm opacity-80">Version ${newVersion} is ready</div>
    </div>
    <button class="btn btn-sm btn-primary update-refresh-btn">
      <i class="fas fa-redo mr-1"></i> Refresh
    </button>
    <button class="btn btn-sm btn-ghost update-dismiss-btn">
      <i class="fas fa-times"></i>
    </button>
  `;
  
  container.appendChild(toast);
  
  // Stop all clicks on the toast from propagating to elements behind it
  toast.addEventListener('click', (e) => {
    e.stopPropagation();
    e.preventDefault();
  });
  
  // Bind click handlers properly (not using inline onclick)
  const refreshBtn = toast.querySelector('.update-refresh-btn');
  if (refreshBtn) {
    refreshBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      e.stopImmediatePropagation();
      e.preventDefault();
      // Navigate to dashboard first, then reload to avoid loading broken forms
      window.location.hash = 'dashboard';
      window.location.reload();
    }, { capture: true });
  }
  
  const dismissBtn = toast.querySelector('.update-dismiss-btn');
  if (dismissBtn) {
    dismissBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      e.stopImmediatePropagation();
      e.preventDefault();
      toast.remove();
    }, { capture: true });
  }
}

/**
 * Create toast container if it doesn't exist
 * Uses the same container as ToasterService for consistency
 */
function createToastContainer(): HTMLElement {
  let container = document.getElementById('toast-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'toast-container';
    container.className = 'toast toast-top toast-end';
    // Explicit inline styles to ensure proper z-index and click handling
    container.style.cssText = `
      position: fixed;
      top: 1rem;
      right: 1rem;
      z-index: ${ZIndex.TOAST};
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 0.5rem;
      pointer-events: none;
    `;
    document.body.appendChild(container);
  }
  return container;
}

/**
 * Initialize version checker
 * Call this once on app startup
 * Safe to call multiple times - will only initialize once
 */
export async function initVersionChecker(): Promise<void> {
  // Prevent multiple initializations (can happen with HMR or multiple login paths)
  if (isInitialized) {
    logger.debug('Version checker already initialized, skipping');
    return;
  }
  isInitialized = true;
  
  // Clear the "toast shown" flag when a new session starts
  // (but keep it during HMR so we don't show multiple toasts)
  // Only clear if the version in sessionStorage doesn't match what we're about to load
  const storedVersion = sessionStorage.getItem('flexiva_current_version');
  
  // Get initial version
  currentVersion = await fetchVersion();

  if (!currentVersion) {
    logger.warn('Could not load version.json - version checking disabled');
    return;
  }
  
  // If version changed, clear the toast shown flag (user refreshed to get the update)
  if (storedVersion && storedVersion !== currentVersion) {
    setUpdateToastShown(false);
  }
  sessionStorage.setItem('flexiva_current_version', currentVersion);

  logger.info(`App version: ${currentVersion}`);

  // Clear any existing interval before setting a new one
  if (checkIntervalId) {
    window.clearInterval(checkIntervalId);
  }
  
  // Start periodic checking
  checkIntervalId = window.setInterval(checkForUpdate, CHECK_INTERVAL_MS);

  // Also check when tab becomes visible (user returns to tab)
  // Use a named function so we can avoid duplicate listeners
  const visibilityHandler = () => {
    if (document.visibilityState === 'visible') {
      checkForUpdate();
    }
  };
  
  // Remove existing listener before adding (prevents duplicates)
  document.removeEventListener('visibilitychange', visibilityHandler);
  document.addEventListener('visibilitychange', visibilityHandler);
}

/**
 * Stop version checking (for cleanup)
 */
export function stopVersionChecker(): void {
  if (checkIntervalId) {
    window.clearInterval(checkIntervalId);
    checkIntervalId = null;
  }
}

/**
 * Get the current app version
 */
export function getAppVersion(): string | null {
  return currentVersion;
}
